/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { Component, EventEmitter, Input, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { MatCellDef, MatColumnDef, MatHeaderCellDef } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { TableMultiSelectModel } from '../../../models/tableMultiSelect.model';
import { TableService } from '../../../services';

@Component({
    selector: 'app-column',
    templateUrl: './tableColumn.component.html',
    styleUrls: ['./tableColumn.component.scss', '../table.component.scss']
})

export class TableColumnComponent implements OnInit {
    /**
     * Set the column name.
     * @param prop
     */
    @Input() set prop(prop: string) {
        if (prop === 'select' || prop === 'actions') {
            this.disableSort = true;
        }
        this._prop = prop;
    }

    /**
     * Set all rows checked.
     * @param checked
     */
    @Input() set allRowsChecked(checked: boolean) {
        this._allRowsChecked = checked;
    }

    /**
     * Set the data in the column so the select all component has a reference,
     * @param data
     */
    @Input() set data(data: any[]) {
        this._data = data;
    }

    /**
     * To store the data for each row and column in the table.
     */
    @Input() public label: any;

    /**
     * Should column position be fixed.
     */
    @Input() public stickyColumn: boolean = false;

    /**
     * Custom template for cell.
     */
    @Input() set cellTemplate(template: TemplateRef<any>) {
        this._cellTemplate = template;
    }

    /**
     * Hide column flag.
     */
    @Input() set columnHidden(hidden: boolean) {
        this._columnHidden = hidden || false;
    }

    /**
     * Disable the sorting functionality for the column.
     */
    @Input() set disableSort(disableSort: boolean) {
        this._disableSort = disableSort;
    }

    /**
     * Set width on column
     * @param width
     */
    @Input() set width(width: string) {
        this._width = width;
    }

    /**
     * Set the flex grow value on the column.
     * @param flexValue
     */
    @Input() set flexGrow(flexValue: number) {
        this._flexGrow = flexValue;
    }

    /**
     * Set the alignment for the content of any given column e.g. right, left etc.
     * @param alignment
     */
    @Input() set cellAlignment(alignment: 'right' | 'left' | 'center') {
        this._cellAlignment = alignment || 'left';
    }

    /**
     * Allow parent component to change the max width of a column.
     */
    @Input() public maxWidth: number = 400;

    /**
     * Toggle all rows emitter.
     */
    @Output() public toggleEmitter: EventEmitter<any> = new EventEmitter();

    /**
     * Column definition.
     */
    @ViewChild(MatColumnDef, { static: true }) columnDef: MatColumnDef;

    /**
     * Cell definition.
     */
    @ViewChild(MatCellDef, { static: true }) cellDef: MatCellDef;

    /**
     * Header cell definition.
     */
    @ViewChild(MatHeaderCellDef, { static: true }) headerCellDef: MatHeaderCellDef;

    /**
     * Store the mat sort definition.
     */
    @ViewChild(MatSort) public sort: MatSort;

    /**
     * Sorting event emitter.
     */
    @Output() sortChange = new EventEmitter<any>();

    /**
     * Store the datasource id to compare with the table stored in the service.
     */
    public dataSourceId: string;

    /**
     * Store the text and action of the select all.
     */
    public selectAll: TableMultiSelectModel[];

    /**
     * Store container width.
     */
    public containerWidth: number;

    /**
     * Variable for name setter.
     * @private
     */
    private _prop: string;

    /**
     * Variable for custom cell template setter/getter;.
     * @private
     */
    private _cellTemplate: TemplateRef<any>;

    /**
     * Disable sort getter/setter.
     * @private
     */
    private _disableSort: boolean = false;

    /**
     * Width variable for getter/setter.
     * @private
     */
    private _width: string;

    /**
     * Store flex grow value.
     * @private
     */
    private _flexGrow: number;

    /**
     * Store column style.
     * @private
     */
    private _columnStyle: any;

    /**
     * Store all rows checked value.
     * @private
     */
    private _allRowsChecked: boolean = false;

    /**
     * Store column hidden value.
     * @private
     */
    private _columnHidden: boolean;

    /**
     * Store cell alignment for getter.
     * @private
     */
    private _cellAlignment: 'right' | 'left' | 'center' = 'left';

    /**
     * Store the data from the parent.
     * @private
     */
    private _data: any[];

    constructor(
        public tableService: TableService
    ) {
    }

    /**
     * Column name getter.
     */
    get prop() {
        return this._prop;
    }

    /**
     * Get the template for the cell.
     */
    get cellTemplate() {
        return this._cellTemplate;
    }

    /**
     * All rows checked getter.
     */
    get allRowsChecked() {
        return this._allRowsChecked;
    }

    get dataSource() {
        return this.tableService.dataSourceLookup[this.dataSourceId].dataSource;
    }

    get selectedRows() {
        return this.dataSource?.selectedRows;
    }

    /**
     * Disable sort getter.
     */
    get disableSort() {
        return this._disableSort;
    }

    /**
     * Column width getter.
     */
    get width() {
        return this._width;
    }

    /**
     * Flex grow getter.
     */
    get flexGrow() {
        return this._flexGrow;
    }

    /**
     * Column style setter.
     */
    set columnStyle(style: any) {
        this._columnStyle = this.setStyle(style['flex-grow']);
    }

    /**
     * Column style getter.
     */
    get columnStyle() {
        return this._columnStyle;
    }

    /**
     * Column hidden getter.
     */
    get columnHidden() {
        return this._columnHidden;
    }

    /**
     * Cell alignment getter.
     */
    get cellAlignment() {
        return this._cellAlignment;
    }

    /**
     * Get data from parent.
     */
    get data() {
        return this._data;
    }

    public ngOnInit(): void {
        if (this.columnHidden === undefined) {
            this.columnHidden = false;
        }

        this.label = this.getFormattedName();

        this.setColumnData();
        this.containerWidth = this.getTableContainerWidth();
    }

    /**
     * Emit the change in the sort to the parent.
     */
    public emitSortToParent(): void {
        this.sortChange.emit(this.sort);
    }

    /**
     * Toggle the checkboxes on the row/s.
     * @param $event
     */
    public toggleAllRows($event: any): void {
        const checked = $event.target.checked;
        this.toggleEmitter.emit(checked);
    }

    /**
     * Get formatted name.
     */
    private getFormattedName(): string {
        return (this.label === '' || this.prop === 'actions' || this.prop === 'select') ? '' : this.label;
    }

    /**
     * Set data for column
     * @private
     */
    private setColumnData() {
        this.columnDef.name = this.prop;
        this.columnDef.cell = this.cellDef;
        this.columnDef.headerCell = this.headerCellDef;
    }

    /**
     * Set the style on the column.
     * @param flexGrow
     * @private
     */
    private setStyle(flexGrow?: number): any {
        if (this.prop === 'actions' || this.prop === 'select') {
            return;
        }

        const flexValue = flexGrow || 1;
        const maxWidth = this.convertFlexGrowToWidth(flexValue);

        return {
            'min-width': maxWidth,
        };
    }

    /**
     * Convert the flex grow property to a width.
     * @param flexGrowValue
     * @private
     */
    private convertFlexGrowToWidth(flexGrowValue: number): string {
        const percentage = (flexGrowValue / this.tableService.columnCount) * 100;
        this.tableService.widthAvailable -= percentage;

        return this.calculateWidth(`${percentage}%`);
    }

    /**
     * Calculate the width of the column.
     * @param width
     * @private
     */
    private calculateWidth(width: any): string {
        if (width.includes('px')) {
            return width;
        }

        const tableWidth = this.getTableContainerWidth();
        const columnWidth = this.cleanInput(width);
        const formattedWidth = Math.floor(tableWidth * (columnWidth / 100));

        return formattedWidth.toString() + 'px';
    }

    /**
     * Get the table container width.
     * @private
     */
    private getTableContainerWidth(): number {
        return document.querySelector('.table-container')?.clientWidth || 1670;
    }

    /**
     * Remove percent character from param and convert to int.
     * @param number
     * @private
     */
    private cleanInput(number: any): any {
        if (typeof number === 'string') {
            if (number.includes('%')) {
                number.replace('%', '');
                number = parseInt(number, 10);
            }
        }

        return number;
    }
}
